/**
 * 再認証 (AAL2) の共通ハンドリング
 *
 * `@SecureApi` を付与した自社エンドポイントは、AAL2 / 直近の認証要件を満たさない
 * リクエストを `HTTP 401 + code` (AAL2_REQUIRED / MFA_REAUTH_REQUIRED) で弾く。
 * その 401 を共通の認証エラーハンドラ (handleAuthErrorResponse) が `ReauthRequiredError`
 * に変換し、機密操作の呼び出しを `handleApiErrorWithReauth` でラップしておくと、
 * AAL2 再認証モーダルを表示 → 成功後に元操作を再実行する。
 *
 * Ory 用の `handleOryErrorWithReauth` (lib/auth/ory/service) と対になる、自社 API 用。
 * transport 非依存のため openapi-fetch / oRPC いずれの呼び出しにも使える。
 */
import { ERROR_CODE } from './error-code';
import { ReauthRequiredError } from './error.reauth-required';

const REAUTH_REQUIRED_CODES: ReadonlySet<string> = new Set([
  ERROR_CODE.AAL2_REQUIRED,
  ERROR_CODE.MFA_REAUTH_REQUIRED,
]);

/**
 * 再認証を要求するエラーコードかどうかを判定する。
 */
export const isReauthRequiredCode = (code: unknown): boolean =>
  typeof code === 'string' && REAUTH_REQUIRED_CODES.has(code);

/**
 * 進行中の再認証フロー。`GlobalAuthModal` は単一の resolver しか保持しないため、
 * 複数の機密操作が同時に `ReauthRequiredError` で失敗しても openModal は一度だけ呼び、
 * 全 caller が同じ Promise を待つ（集約しないと先発の Promise が後発の openModal で
 * 上書きされ宙吊りになる）。成功・キャンセルいずれの settle 後も null に戻す。
 */
let inflightReauth: Promise<void> | null = null;

const requestReauth = (message: string | undefined): Promise<void> => {
  inflightReauth ??= (async () => {
    // GlobalAuthModal は features/auth（React ツリー）に属し、lib/api → features の
    // 静的依存（循環依存）を避けるため dynamic import で参照する。
    // 既存の handleOryErrorWithReauth と同じパターン。
    const { useGlobalAuthModal } =
      await import('@/features/auth/components/GlobalAuthModal');
    await useGlobalAuthModal.getState().openModal('aal2', { message });
  })().finally(() => {
    inflightReauth = null;
  });
  return inflightReauth;
};

/**
 * API 呼び出しが `ReauthRequiredError`（@SecureApi の 401 + code）で失敗した場合に、
 * AAL2 再認証モーダルを開き、成功後に元の呼び出しを **1回だけ** 再実行する。
 * 機密操作の query/mutation 関数をこれでラップする。
 *
 * 再実行は元の `apiCall` を呼び直すため、Request の使い回しや body 消費を考慮しなくてよい。
 * 再認証後も再び `ReauthRequiredError` なら（= セッション切れ等）そのまま throw され、
 * 通常のエラーハンドリング（queryClient）に委ねる。ユーザーがモーダルをキャンセルした
 * 場合は `openModal` が reject するため再実行されない。
 *
 * @example
 * export const updateMfa = (body: MfaSettingsReq) =>
 *   handleApiErrorWithReauth(async () => {
 *     const res = await apiClient.PATCH('/v2/settings/mfa/email', { body });
 *     return getDataOrThrowApiError(res);
 *   });
 */
export async function handleApiErrorWithReauth<T>(
  apiCall: () => Promise<T>,
): Promise<T> {
  try {
    return await apiCall();
  } catch (error) {
    if (!(error instanceof ReauthRequiredError)) {
      throw error;
    }
    // 並行する再認証は1モーダルに集約（キャンセル時は AuthCancelledError が伝播し再実行しない）。
    await requestReauth(error.displayMessage);
    return await apiCall();
  }
}
